///<reference path="../../../../../../motors/dist/types/src/index.d.ts"/>
import Action from "./class.Action";
import {Parsers} from "../class.TransporterParser.js";
import * as Input from "../class.TransporterInput";
import * as Utils from "../class.TransporterUtils";
import * as ActionUtils from "./class.ActionUtils";

/**
 * Creates a Motor and parses data if necessary.
 */
export default class Motor extends Action {
	constructor(options) {
		super(options);

		// A reference to a Motor controller.
		this.controller = null;
		// A reference to the active bubble containing our Motor.
		this.motorView = null;
		// A reference to the active bubble containing our response.
		this.responseView = null;

		// A list of our created Inputs.
		this.inputs = [];

		// A string version of the student's response.
		this.response = null;
		// An html version of the student's response.
		this.preview = null;

		// Whether the student gave up.
		this.gaveUp = false;
		// The attempt number we're on.
		this.attempt = 0;
		// Whether the student passed the question.
		this.passed = false;

		if (Motors) {
			// Override this deprecated motor.
			if (this.model.driver === "ToggleGridDriver") {
				this.model.driver = "HundredsChartDriver";
			}

			// Render the motor.
			this.renderMotor();
		} else {
			this.onAdvance(this.$anchor);
		}
	}

	/**
	 * Returns promise which resolves with the index of an option
	 * which matches the student's response.
	 *
	 * Resolves -1 if the answer was incorrect.
	 *
	 * @param response The student's response.
	 * @returns A promise resolving with the index of the matching option, or -1.
	 */
	parseResponse(response) {
		return new Promise(async (resolve, reject) => {
			var index = -1;
			var options = this.model.options;
			if (options.length === 0) {
				resolve(index);
			}
			try {
				index = await Parsers.auto.indexOfResponse(options, response);
				resolve(index);
			} catch (e) {
				reject(e);
			}
		});
	}

	/**
	 * Traverse the flow after this problem has been passed.
	 * @param {*} $problemAnchor
	 */
	passProblem($problemAnchor) {
		this.passed = true;
		var trigger = "trigger";
		var hasGiveUpTrigger =
			this.model["give-up-trigger"] && this.model["give-up-trigger"].length;
		if (this.gaveUp && hasGiveUpTrigger) {
			trigger = "give-up-trigger";
		}
		this.runnerController.createAndTraverseFlow({
			pointer: this.pointer,
			item: this.model,
			flow: [{traverse: trigger}],
			onAdvance: ($newAnchor) => {
				this.onAdvance($newAnchor);
			},
			onBypass: () => {
				this.onBypass();
			},
			$anchor: $problemAnchor,
			context: this.context,
		});
	}

	async renderMotor($anchor) {
		if (!$anchor) {
			$anchor = this.$anchor;
		}

		if (this.model.center) {
			this.motorView = this.layoutView.placeBubble(
				$anchor,
				$anchor.is(this.layoutView.$el) ? "append" : "after",
				"center"
			);
			const $motorDiv = $("<div />");
			this.motorView.appendContent($motorDiv);

			for (let prim of this.model.body) {
				if (prim.html || prim.text) {
					this.motorView
						.appendVirtualBubble()
						.appendContent(
							$(`<div style="text-align:center;"/>`).html(
								prim.html || prim.text
							)
						);
				}
			}
		} else {
			// We must render our prompt, and then set up our problem bubble.
			let lastPrim;
			for (let prim of this.model.body) {
				if (prim.html || prim.text) {
					let anchor;
					let method;
					if ($anchor.is(this.layoutView)) {
						method = "append";
						anchor = this.layoutView.$el;
					} else {
						method = "after";
						if (lastPrim) {
							anchor = lastPrim.$el;
						} else {
							anchor = $anchor;
						}
					}
					lastPrim = this.layoutView.placeBubble(anchor, method, "teacher");
					lastPrim.appendContent($("<div />").html(prim.html || prim.text));
					/* eslint-disable-next-line no-await-in-loop */
					await this.animationController.FadeIn.start({
						bubbleView: lastPrim,
					}).scroll();
				}
			}
			let $newAnchor = lastPrim ? lastPrim.$el : $anchor;
			this.motorView = this.layoutView.placeBubble(
				$newAnchor,
				$newAnchor.is(this.layoutView.$el) ? "append" : "after",
				"whiteboard"
			);
		}

		const $motorDiv = $("<div />");
		this.motorView.appendContent($motorDiv);

		/**
		 * Build the motor.
		 */
		if (this.model.driver) {
			if (Motors.Drivers[this.model.driver]) {
				// JSON Driver uses the json property.
				const args = [];
				if (this.model.driver === "JSONDriver" && this.model.json) {
					args.push({
						json: this.model.json,
					});
				}
				// Instantiate Motor
				const driver = new Motors.Drivers[this.model.driver](...args);
				this.controller = new Motors.Controller({driver});
			} else {
				$motorDiv.text(`Driver ${this.model.driver} is missing.`);
			}
		} else {
			$motorDiv.text(`Driver is undefined.`);
		}

		/**
		 * Build the parsing stuff.
		 */
		const answerKey = this.model.answer_key;
		const previewKey = this.model.preview_key;
		let advance = false;
		let loadCachedResponse = false;
		if (this.model.type === "motor-parsed") {
			if (answerKey.length === 0) {
				advance = true;
			} else if (!this.controller.driver.inputs.portMap[answerKey]) {
				advance = true;
			} else {
				if (this.inputController.motorCache[this.pointerStr + this.attempt]) {
					loadCachedResponse = true;
				} else {
					this.controller.driver.addResolveListener((data) => {
						if (data[answerKey]) {
							this.inputController.motorCache[this.pointerStr + this.attempt] =
								JSON.stringify(data);
							this.renderResponseBubble(data[answerKey], data[previewKey]);
						}
					});
				}
			}
		} else {
			// Unparsed, so continue with the script.
			advance = true;
		}

		/**
		 * Move on with our life.
		 */
		this.controller.start(
			this.parsedObjectOrNull(this.model.startSignal) || {}
		);
		Motors.render(this.controller.render(), $motorDiv[0]);
		await this.animationController.FadeIn.start({
			bubbleView: this.motorView,
		}).scroll();
		$motorDiv
			.attr("tabindex", 0)
			.focus(() => {
				$motorDiv.css("outline", "none");
			})
			.keypress((event) => {
				// Bind enter key to submit.
				if (event.keyCode === 13) {
					if (this.responseView) {
						this.responseView.$el
							.find(`#${this.pointerStr}Attempt${this.attempt}Submit`)
							.trigger("click");
					}
				}
			})
			.focus();
		if (advance) {
			this.onAdvance();
		} else if (loadCachedResponse) {
			const data = JSON.parse(
				this.inputController.motorCache[this.pointerStr + this.attempt]
			);
			this.renderResponseBubble(data[answerKey], data[previewKey]);
		}
	}

	/**
	 * Renders the student submission bubble.
	 * @param {*} response The response data.
	 * @param {*} [preview] The preview data.
	 */
	async renderResponseBubble(response, preview) {
		this.response = response;
		this.preview = preview;

		if (!this.responseView) {
			// Ignore empty responses.
			if (this.isResponseEmpty(this.response)) {
				return;
			}

			// Create the new bubble.
			this.responseView = this.layoutView.placeBubble(
				this.motorView.$el,
				"after",
				"student"
			);
			const $submitDiv = $(`<div class="btn-submit-container"/>`);
			this.responseView.setContent($('<div class="response"></div>'));
			this.responseView.appendContent($submitDiv);
			this.responseView.$el.addClass("short-answer");
			if (this.model.render_jax) {
				this.renderJax(
					this.responseView.$el.find(".response"),
					this.preview !== undefined
						? String(this.preview)
						: String(this.response)
				);
			} else {
				this.responseView.$el
					.find(".response")
					.text(
						this.preview !== undefined
							? String(this.preview)
							: String(this.response)
					);
			}

			// Create the Give Up button.
			$submitDiv.addClass("has-hint");
			const surrenderButton = new Input.Button({
				attributes: {
					inputController: this.inputController,
					className: "btn-like",
					tagName: "a",
					target: this.pointerStr + "Attempt" + this.attempt + "Surrender",
				},
			});
			this.inputs.push(surrenderButton);
			surrenderButton.$el.html("Give Up");
			surrenderButton.$el.addClass("color3");
			$submitDiv.append(surrenderButton.$el);

			// Bind the give up button's action.
			surrenderButton.onClick({
				once: true,
				callback: async () => {
					this.attempt++;

					// Deactivate buttons
					while (this.inputs.length > 0) {
						this.inputs.pop().off();
					}

					// Deactive motors.
					if (this.controller) {
						this.controller.stop();
						this.controller = null;
					}

					this.responseView.$el.removeClass("short-answer");
					this.responseView.setContent("I give up.");
					await this.animationController.FadeIn.start({
						bubbleView: this.responseView,
					}).scroll();

					this.gaveUp = true;
					this.passProblem(this.responseView.$el);
				},
			});

			// Create the submit button
			const submitButton = new Input.Button({
				attributes: {
					inputController: this.inputController,
					className: "btn-like",
					tagName: "a",
					target: this.pointerStr + "Attempt" + this.attempt + "Submit",
				},
			});
			this.inputs.push(submitButton);
			submitButton.$el.html("Submit");
			submitButton.$el.addClass("color2");
			$submitDiv.append(submitButton.$el);

			// Bind the submit button
			submitButton.onClick({
				once: true,
				callback: async () => {
					this.attempt++;
					// Deactivate buttons
					while (this.inputs.length > 0) {
						this.inputs.pop().off();
					}

					// Deactive motors.
					this.controller.stop();
					this.controller = null;

					// Place the student response bubble.
					if (this.isResponseEmpty(this.response)) {
						this.responseView.setContent("<em>No response.</em>");
					} else {
						$submitDiv.remove();
					}
					this.responseView.$el.removeClass("short-answer");
					await this.animationController.FadeIn.start({
						bubbleView: this.responseView,
					}).scroll();
					const responseBubble = this.responseView;

					// Parse the response, assume incorrect if there's a
					// failure, such as bad/failed AJAX.
					var key, option;
					try {
						key = await this.parseResponse(this.response.toString());
						option = this.model.options[key];
					} catch (e) {
						key = -1;
						option = undefined;
					}
					if (option && option.trigger) {
						if (option.is_correct) {
							responseBubble.pass();
						} else {
							responseBubble.fail();
						}
						// Traverse the new flow for this response.
						this.runnerController.createAndTraverseFlow({
							pointer: this.pointer.concat(["options", key]),
							context: this.context,
							item: option,
							flow: [{traverse: "trigger"}],
							onAdvance: ($newAnchor) => {
								if (option.is_correct) {
									this.passProblem($newAnchor);
								} else {
									this.responseView = null;
									this.renderMotor($newAnchor);
								}
							},
							onBypass: () => {
								this.onBypass();
							},
							$anchor: this.responseView.$el,
						});
					} else if (option && option.is_correct) {
						// A correct option with no trigger.
						responseBubble.pass();
						var teacherBubbleView = this.layoutView.placeBubble(
							this.responseView.$el,
							"after",
							"teacher"
						);

						teacherBubbleView.setContent(
							ActionUtils.getRandomTeacherResponse("positive")
						);

						await this.animationController.FadeIn.start({
							bubbleView: teacherBubbleView,
						}).scroll();

						this.passProblem(teacherBubbleView.$el);
					} else if (this.model.options.length > 0) {
						// A wrong option with no trigger.
						responseBubble.fail();
						await this.renderTeacherResponse(this.responseView.$el);
					} else {
						// There are no right/wrong options! So just pass it.
						responseBubble.pass();
						this.passProblem(this.responseView.$el);
					}
				},
			});

			await this.animationController.FadeIn.start({
				bubbleView: this.responseView,
			}).scroll();
			this.inputController.request({enableAnimations: true});
		} else {
			if (this.isResponseEmpty(this.response)) {
				this.responseView.$el.find(".response").html("<em>No response.</em>");
			} else {
				if (this.model.render_jax) {
					this.renderJax(
						this.responseView.$el.find(".response"),
						this.preview !== undefined
							? String(this.preview)
							: String(this.response)
					);
				} else {
					this.responseView.$el
						.find(".response")
						.text(
							this.preview !== undefined
								? String(this.preview)
								: String(this.response)
						);
				}
			}
			this.inputController.request({enableAnimations: true});
		}
	}

	/**
	 * A helper function which will render the MathJax into a target.
	 */
	async renderJax($target, jax) {
		let $renderBox = $(`<div class="render-box"/>`).css({
			position: "fixed",
			top: "-100vh",
			left: "-100vw",
			height: "100vh",
			width: "100vw",
			overflow: "none",
			"white-space": "nowrap",
			visibility: "hidden",
			"pointer-events": "none",
			"z-index": "-99999",
		});
		this.mainView.$el.append($renderBox);
		$renderBox.text(jax);
		await Utils.requestMathJaxRender($renderBox[0]);
		const $children = $renderBox.children();
		if ($children.length > 0) {
			$target.html("").append($children);
		}
		$renderBox.remove();
	}

	/**
	 * Render's a default response from the teacher's side.
	 * Mutates `this.activeBubbleView`.
	 *
	 * @param {*} $anchor
	 */
	async renderTeacherResponse($anchor) {
		var teacherBubbleView = this.layoutView.placeBubble(
			$anchor,
			"after",
			"teacher"
		);

		teacherBubbleView.setContent(
			ActionUtils.getRandomTeacherResponse("negative")
		);

		await this.animationController.FadeIn.start({
			bubbleView: teacherBubbleView,
		}).scroll();
		this.responseView = null;
		this.renderMotor(teacherBubbleView.$el);
	}

	/**
	 * Returns true if the response should be considered empty.
	 * E.g. an array of empty strings
	 *
	 * Add new constraints to this function as necessary.
	 * @param {*} response
	 * @returns boolean
	 */
	isResponseEmpty(response) {
		if (_.isArray(response)) {
			if (response.join("") === "") {
				return true;
			}
		}
		if (_.isNaN(response)) {
			return true;
		}
	}

	/**
	 * Accepts a JSON string and returns either a parsed object or null.
	 */
	parsedObjectOrNull(json) {
		try {
			const parsed = JSON.parse(json);
			return _.isObject(parsed) ? parsed : null;
		} catch (e) {
			return null;
		}
	}
}
